import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TxnQuery, TxnService } from '@b3networks/api/inbox';
import { SessionsService } from '@b3networks/api/kbbot';
import {
  ConversationGroup,
  ConversationGroupQuery,
  ConversationGroupService,
  ConversationGroupUI
} from '@b3networks/api/workspace';
import { CHAT_PUBLIC_PREFIX, X_B3_HEADER } from '@b3networks/shared/common';
import { ToastService } from '@b3networks/shared/ui/toast';
import { HashMap, ID } from '@datorama/akita';
import { of } from 'rxjs';
import { filter, finalize, map, tap } from 'rxjs/operators';
import { ChannelHyperspaceQuery } from '../channel-hyperspace/channel-hyperspace.query';
import { ChannelHyperspaceService } from '../channel-hyperspace/channel-hyperspace.service';
import { ChannelQuery } from '../channel/channel.query';
import { ChannelService } from '../channel/channel.service';
import { ChatMessage } from '../chat-message/chat-message.model';
import { ConvoType } from '../enums.model';
import { CacheMessageQuery } from './cache-message/cache-message.query';
import { CacheMessageService } from './cache-message/cache-message.service';
import {
  ConfigStore,
  FilterConvoMessageRangeRequest,
  FilterConvoMessageReq,
  HistoryMessage,
  HistoryMessageRange
} from './history-message.model';
import { HistoryMessageQuery } from './history-message.query';
import { HistoryMessageStore } from './history-message.store';
import { SendWhatsAppRequest } from './whatsapp-integration';

@Injectable({ providedIn: 'root' })
export class HistoryMessageService {
  constructor(
    private historyMessageStore: HistoryMessageStore,
    private historyMessageQuery: HistoryMessageQuery,
    private http: HttpClient,
    private convoQuery: ConversationGroupQuery,
    private convoService: ConversationGroupService,
    private channelService: ChannelService,
    private channelQuery: ChannelQuery,
    private channelHyperspaceQuery: ChannelHyperspaceQuery,
    private channelHyperspaceService: ChannelHyperspaceService,
    private cacheMessageService: CacheMessageService,
    private cacheMessageQuery: CacheMessageQuery,
    private toastService: ToastService,
    private txnQuery: TxnQuery,
    private txnService: TxnService,
    private sessionsService: SessionsService
  ) {}

  private createHttpParams(req: FilterConvoMessageReq): HttpParams {
    let params = new HttpParams();
    if (req.limit) {
      params = params.set('limit', String(req.limit));
    }
    if (req.toMillis) {
      params = params.set('toMillis', String(req.toMillis));
    }
    if (req.fromMillis) {
      params = params.set('fromMillis', String(req.fromMillis));
    }
    if (req.conversations && req.conversations.length > 0) {
      params = params.set('conversations', req.conversations.join(','));
    }
    return params;
  }

  private processHistoryModel(model: HistoryMessage, req: FilterConvoMessageReq, convoStateUi: any): void {
    if (!convoStateUi.toMillis || model.toMillis >= convoStateUi.toMillis) {
      convoStateUi.toMillis = model.toMillis;
    }
    if (!convoStateUi.fromMillis || model.fromMillis <= convoStateUi.fromMillis) {
      convoStateUi.fromMillis = model.fromMillis;
      convoStateUi.hasMore = req.limit ? model.messages.length === +req.limit : false;
    }
    this.upsertManyMessages(model.messages);
  }

  get(convoId: string, req: FilterConvoMessageReq) {
    this.historyMessageStore.setLoading(true);
    const params = this.createHttpParams(req);
    return this.http
      .get<HistoryMessage>(`workspace/private/v1/group-conversations/${convoId}/history`, { params })
      .pipe(
        filter(history => !!history?.messages),
        tap(history => history.messages.sort((a, b) => a.ts - b.ts)),
        map(history => {
          const model = new HistoryMessage(history);
          const convoStateUi = this.convoQuery.getConvoUiState(convoId);
          this.processHistoryModel(model, req, convoStateUi);
          this.convoService.updateConvoViewState(convoId, convoStateUi);
          return model;
        }),
        finalize(() => this.historyMessageStore.setLoading(false))
      );
  }

  getChannelHistory(channelId: string, req: FilterConvoMessageReq) {
    this.historyMessageStore.setLoading(true);
    const params = this.createHttpParams(req);
    return this.http.get<HistoryMessage>(`/public/_tc/channel/${channelId}/history`, { params }).pipe(
      map(history => {
        const model = new HistoryMessage(history);
        const convoStateUi = this.channelQuery.getChannelUiState(channelId);
        if (convoStateUi) {
          this.processHistoryModel(model, req, convoStateUi);
          this.channelService.updateChannelViewState(channelId, convoStateUi);
        }
        return model;
      }),
      finalize(() => this.historyMessageStore.setLoading(false))
    );
  }

  getV2(convoId: string, req: FilterConvoMessageReq) {
    this.historyMessageStore.setLoading(true);
    const params = this.createHttpParams(req);
    return this.http.get<HistoryMessage>(`workspace/private/v2/conversations/${convoId}/histories`, { params }).pipe(
      map(history => new HistoryMessage({ ...history, convoUuid: convoId })),
      tap(history => {
        const convoStateUi = <ConversationGroupUI>{
          fromMillis: history.fromMillis,
          hasMore: history.messages.length === req.limit
        };
        if (this.isEmptyStore()) {
          this.historyMessageStore.set(history.messages);
          convoStateUi.toMillis = history.toMillis;
        } else {
          this.historyMessageStore.add(history.messages, { loading: false });
        }
        this.convoService.updateConvoViewState(convoId, convoStateUi);
      }),
      finalize(() => this.historyMessageStore.setLoading(false))
    );
  }

  getV1(convoGroup: ConversationGroup, limit: number) {
    this.historyMessageStore.setLoading(true);
    const params = new HttpParams().set('limit', String(limit));
    return this.http
      .get<HistoryMessage>(
        `workspace/private/v1/customers/conversations/${convoGroup.publicConversationId}/histories`,
        { params }
      )
      .pipe(
        map(history => new HistoryMessage({ ...history, convoUuid: convoGroup.conversationGroupId })),
        tap(history => {
          if (this.isEmptyStore()) {
            this.historyMessageStore.set(history.messages);
          } else {
            this.historyMessageStore.add(history.messages, { loading: false });
          }
        }),
        finalize(() => this.historyMessageStore.setLoading(false))
      );
  }

  getTxnHistoryV2(txnUuid: string, req: FilterConvoMessageReq) {
    this.historyMessageStore.setLoading(true);
    return this.http.post<string>(`inbox/private/v2/txn/${txnUuid}/messages/_get`, req).pipe(
      map(history => {
        const model = new HistoryMessage(history);
        const convoStateUi = this.convoQuery.getConvoUiState(req.convoId) || {};
        this.processHistoryModel(model, req, convoStateUi);
        this.convoService.updateConvoViewState(req.convoId, convoStateUi);
        return model;
      }),
      finalize(() => this.historyMessageStore.setLoading(false))
    );
  }

  getWhatsappHistory(convoId: string, req: FilterConvoMessageReq) {
    this.historyMessageStore.setLoading(true);
    const params: any = {
      convoIds: req.conversations ? [...req.conversations] : [],
      fromMillis: req.fromMillis,
      toMillis: req.toMillis,
      limit: req.limit
    };
    return this.http.post<HistoryMessage>(`${CHAT_PUBLIC_PREFIX}/legacy/history`, params).pipe(
      map(history => {
        const model = new HistoryMessage(history);
        const convoStateUi = this.convoQuery.getConvoUiState(convoId);
        this.processHistoryModel(model, req, convoStateUi);
        this.convoService.updateConvoViewState(convoId, convoStateUi);
        return model;
      }),
      finalize(() => this.historyMessageStore.setLoading(false))
    );
  }

  private setLoadingState(isLoading: boolean, config?: ConfigStore) {
    if (!config?.noLoading) {
      this.historyMessageStore.setLoading(isLoading);
    }
  }

  private updateConvoStateUi(convoStateUi: any, model: HistoryMessageRange, req: FilterConvoMessageRangeRequest) {
    if (!req.afterToSize && !req.beforeFromSize) {
      if (!req.to && !req.from) {
        if (!req.isAsc) {
          convoStateUi.hasMoreTop = model.messages.length !== 0;
        } else {
          convoStateUi.hasMoreBottom = model.messages.length !== 0;
        }
      }
      if (req.to && !req.from) {
        convoStateUi.hasMoreTop = model.messages.length !== 0;
      }
      if (req.from && !req.to) {
        convoStateUi.hasMoreBottom = model.messages.length !== 0;
      }
    } else if (req.from === req.to) {
      const index = model.messages.findIndex(x => x.id === req.from);
      convoStateUi.hasMoreTop = index !== 0;
      convoStateUi.hasMoreBottom = index <= model.messages.length - 1;
    }
  }

  getChannelRangeHistory(req: FilterConvoMessageRangeRequest, config: ConfigStore) {
    this.setLoadingState(true, config);
    return this.http.post<HistoryMessageRange>(`/public/v2/_tc/namespace/message/range`, req).pipe(
      finalize(() => this.setLoadingState(false, config)),
      map(histories => {
        const model = new HistoryMessageRange(histories);
        if (!config?.isNoStore) {
          const convoStateUi = this.channelQuery.getChannelUiState(req.convoId);
          if (!convoStateUi) return model;
          this.updateConvoStateUi(convoStateUi, model, req);
          this.upsertManyMessages(model.messages);
          this.channelService.updateChannelViewState(req.convoId, convoStateUi);
        }
        return model;
      })
    );
  }

  getRangeHistoryV3(req: FilterConvoMessageRangeRequest, config: ConfigStore, namespace: string) {
    const headers = new HttpHeaders().append(X_B3_HEADER.Namespace, namespace);
    this.setLoadingState(true, config);
    return this.http.post<HistoryMessageRange>(`/public/v3/namespace/message/range`, req, { headers }).pipe(
      finalize(() => this.setLoadingState(false, config)),
      map(histories => {
        const model = new HistoryMessageRange(histories);
        if (!config?.isNoStore) {
          const convoStateUi = this.channelQuery.getChannelUiState(req.convoId);
          if (!convoStateUi) return model;
          this.updateConvoStateUi(convoStateUi, model, req);
          this.upsertManyMessages(model.messages);
          this.channelService.updateChannelViewState(req.convoId, convoStateUi);
        }
        return model;
      })
    );
  }

  addMessage(message: ChatMessage): boolean {
    let hasMsgClient = false;
    if (message.isStore || message.ct === ConvoType.call) {
      if (message.ct === ConvoType.email) {
        if (message.id) {
          this.historyMessageStore.add(message);
          this.updateLastMessage(message);
        }
        return hasMsgClient;
      }
      if (message.id) {
        hasMsgClient = this.historyMessageQuery.hasEntity(message.unixTsAndUser);
        this.historyMessageStore.remove(message.unixTsAndUser);
        this.historyMessageStore.add(message);
        this.updateLastMessage(message);
      } else {
        this.historyMessageStore.add(message);
      }
    } else {
      console.log(`no store message. Dont need add to store`);
    }
    return hasMsgClient;
  }

  updateBookmarkExpandMap(data: HashMap<ChatMessage>) {
    this.historyMessageStore.update(entity => ({
      ...entity,
      bookmarkExpandMap: {
        ...entity.bookmarkExpandMap,
        ...data
      }
    }));
  }

  updateMessage(message: ChatMessage) {
    this.updateLastMessage(message);
    this.historyMessageStore.update(message.clientId, message);
  }

  updateMessageV2(message: ChatMessage) {
    this.historyMessageStore.update(message.clientId, message);
    if (this.cacheMessageQuery.hasEntity(message.id)) {
      this.cacheMessageService.upsertManyMessages([message]);
    }
  }

  addMessage2Store(message: ChatMessage) {
    this.historyMessageStore.add(message);
    if (this.cacheMessageQuery.hasEntity(message.id)) {
      this.cacheMessageService.upsertManyMessages([message]);
    }
  }

  removeMessage(message: ChatMessage) {
    this.historyMessageStore.remove(message.clientId);
  }

  removeMessageId(messageId: string) {
    this.historyMessageStore.remove(messageId);
  }

  sendWhatsApp(req: SendWhatsAppRequest) {
    if (!!req.message?.text && new Blob([req.message.text]).size > 3 * 1024) {
      this.toastService.error('message-too-large');
      return of(null);
    } else {
      return this.http.post('workspace/private/v1/chat', req);
    }
  }

  sendWhatsAppV2(req: SendWhatsAppRequest) {
    if (!!req.message?.text && new Blob([req.message.text]).size > 3 * 1024) {
      this.toastService.error('message-too-large');
      return of(null);
    } else {
      return this.http.post('workspace/private/v2/chat', req);
    }
  }

  setActive(messageId: string | ID) {
    this.historyMessageStore.setActive(messageId);
  }

  removeActive(messageId: string | ID) {
    this.historyMessageStore.removeActive(messageId);
  }

  cleanupOneConvoMessages(convoUuid: string) {
    this.historyMessageStore.remove((entity: ChatMessage) => entity.channelId === convoUuid);
  }

  cleanupConvoMessages(listConvoLoaded: string[]) {
    this.historyMessageStore.remove((entity: ChatMessage) => {
      return listConvoLoaded.indexOf(entity.channelId) > -1;
    });
  }

  cleanupAllMessage() {
    this.historyMessageStore.remove();
  }

  isEmptyStore() {
    const ids = this.historyMessageStore._value()?.ids;
    return ids == null || ids.length === 0;
  }

  upsertManyMessages(messages: ChatMessage[]) {
    this.historyMessageStore.upsertMany(messages, {
      baseClass: ChatMessage
    });

    messages
      .filter(x => this.cacheMessageQuery.hasEntity(x.id))
      ?.forEach(item => {
        this.cacheMessageService.upsertManyMessages([item]);
      });
  }

  upsertMessage(message: ChatMessage) {
    this.historyMessageStore.upsert(message.id, message, {
      baseClass: ChatMessage
    });

    if (this.cacheMessageQuery.hasEntity(message.id)) {
      this.cacheMessageService.upsertManyMessages([message]);
    }
  }

  private updateLastMessage(message: ChatMessage) {
    switch (message.ct) {
      case ConvoType.direct:
      case ConvoType.groupchat:
      case ConvoType.THREAD:
      case ConvoType.NEWSFEED:
        this.channelService.updateLastMessage(message.channelId, message);
        break;
      case ConvoType.INTERNAL_SPACE:
      case ConvoType.LIVECHAT:
      case ConvoType.sms:
      case ConvoType.email:
        this.convoService.updateLastMessage(message.channelId, message);
        break;
      case ConvoType.whatsapp: {
        this.convoService.updateLastMessage(message.convo, message);
        const txn = this.txnQuery.findTxnByConvo(message.convo);
        if (txn && txn.customerChatUserId === message.user) {
          this.txnService.updateTxn2Store(txn.txnUuid, {
            whatsapp: { ...txn.whatsapp, lastMessageTime: message.ts }
          });
        }
        break;
      }
      case ConvoType.personal:
        break;
      case ConvoType.KB:
        this.sessionsService.updateLastActivityTime(message.convo, message.ts);
        break;
      default:
        console.error(`message ${message.ct} type does not supported yet.`);
        break;
    }
  }
}
